<script setup lang="ts">
import { VueSpinner } from "vue3-spinners";
import CustomButton from "@/components/Button/Button.vue";
import CustomModal from "@/components/CustomModal/CustomModal.vue";
import { PropType, ref, watch } from "vue";
import { useI18n } from "vue-i18n";
import { useTripsStore } from "@/stores/tripsStore";
import boat1 from "@/assets/about/boat-1.png";
import CustomSpinner from "@/components/CustomSpinner/CustomSpinner.vue";
const defaultImage = boat1;

const tripStore = useTripsStore();
const tripUploadInput = ref(null);
const fileUploadError = ref('');
const saveSuccess = ref(false);
const saveError = ref(false);
const isUploading = ref(false);

const props = defineProps({
  show: Boolean,
  name: String,
  backgroundImageUrl: String,
  uuid: String
});


const emit = defineEmits<{
  'on-close': [],
  'on-edit-click': [string]
}>();

const { t } = useI18n();

// Create local editable copy of the name
const editedName = ref(props.name);
const backgroundImageUrl = ref(props.backgroundImageUrl || defaultImage);

watch(() => props.name, (newName) => {
  editedName.value = newName;
});

watch(() => props.backgroundImageUrl, (newUrl) => {
  backgroundImageUrl.value = newUrl || defaultImage;
}, { immediate: true });


function triggerTripUpload() {
  tripUploadInput.value?.click();
}

async function handleTripPhotoUpload(event) {
  fileUploadError.value = '';
  const file = event?.target?.files?.[0];
  if (!file) return;

  const allowedTypes = ['image/jpeg', 'image/png', 'image/jpg'];
  const maxSizeMB = 2;

  if (!allowedTypes.includes(file.type)) {
    fileUploadError.value = "Invalid file type.";
    return;
  }

  if (file.size > maxSizeMB * 1024 * 1024) {
    fileUploadError.value = "File size exceeds 2MB limit.";
    return;
  }

  isUploading.value = true;
  try {
    saveSuccess.value = false;
    saveError.value = false;
    const newImageUrl = await tripStore.updateTripImageStore(file, props.uuid || '');
    emit('on-image-updated', newImageUrl);
    backgroundImageUrl.value = `${newImageUrl}?t=${Date.now()}`;
    console.log('Emitting updated image URL:', newImageUrl);
    saveSuccess.value = true;
  } catch (error) {
    fileUploadError.value = "Image upload failed.";
    saveError.value = true;
    console.error("Image upload failed:", error);
  } finally {
    isUploading.value = false;
  }
}

// Body scroll lock if popup displayed
watch(() => props.show, (newValue) => {
  if (newValue) {
    document.documentElement.style.overflow = 'hidden';
  } else {
    document.documentElement.style.overflow = 'auto';
    // Reset messages when modal is closed
    fileUploadError.value = '';
    saveSuccess.value = false;
    saveError.value = false;
  }
});

</script>

<template>
  <CustomModal :show="show" @close="$emit('on-close')" classes="w-1/6" :mobile-fullscreen="false" :custom-index="9999"  extra-classes="h-fit" drawerHeight="fit-content">
    <div class="flex flex-col p-0 sm:p-6 sm:max-w-[446px]">
      <div class="cursor-pointer self-end hidden sm:block" type="default" @click="$emit('on-close')">
        <img src="@/assets/icons/svg/Close_16x16.svg" alt="Close" width="18" />
      </div>
      <div class="py-3.5 flex flex-col gap-8">
        <div class="responsive-title-4 pb-5 font-semibold">{{ t('tripEditModal.editTrip') }}</div>
<!--        Image-->
        <div class="flex flex-col gap-2">
          <span class=" responsive-body-small text-gray-600">{{ t('tripEditModal.tripImage') }}</span>
          <div class="flex align-center gap-6">
            <div class="imageTrip ">
              <img :src="backgroundImageUrl" alt="Trip Image" class="object-contain rounded-11xl" />
            </div>
            <div class="flex flex-col gap-2.5">
              <CustomButton type="outline-black"
                            size="outlinesmall"
                            class=" responsive-body-small w-fit sm:h-7"
                            :rounded="true" @click="triggerTripUpload">
                {{ t('profile.uploadPhoto') }}
              </CustomButton>
              <span class=" responsive-body-xsmall semiboldText text-wrap">{{ t('profile.fileRequirements') }}</span>
              <CustomSpinner v-if="saveSuccess === false && saveError === false && isUploading" :size="20"/>
            </div>
          </div>
        </div>
        <div v-if="fileUploadError" class="text-notifications-black responsive-body-xsmall mt-2">
          {{ fileUploadError }}
        </div>
        <div v-if="saveSuccess" class="text-notifications-confirmed responsive-body-xsmall">
<!--          {{ t('profile.savedSuccessfully') }}--> {{ t('tripEditModal.success') }}
        </div>
        <div v-if="saveError" class="text-notifications-red responsive-body-xsmall">
          {{ t('profile.saveFailed') }}
        </div>
<!--        Name-->
        <VTextField
            v-model="editedName"
            :label="t('tripEditModal.tripNameLabel')"
            :placeholder="t('tripEditModal.tripNamePlaceholder')"
            variant="underlined"
        />

        <div class="flex gap-4 justify-space-between">
          <CustomButton
              type="black"
              class="self-start responsive-body-medium w-28 h-13"
              :rounded="true"
              :outlined="false"
              @click="$emit('on-edit-click', editedName)">
            {{t('tripEditModal.updateButton') }}
          </CustomButton>
          <CustomButton
              :outlined="true"
              :rounded="true"
              class="hover:bg-black hover:text-white hover:border-black transition-all"
              @click="$emit('on-close')">
            {{ t('tripEditModal.cancelButton') }}
          </CustomButton>
        </div>
      </div>
    </div>
    <input
        type="file"
        accept="image/*"
        ref="tripUploadInput"
        class="hidden"
        @change="handleTripPhotoUpload"
    />
  </CustomModal>
</template>

<style scoped>
.imageTrip{
  width: 60%;
}
</style>
